import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Autocomplete,
  Alert,
  CircularProgress,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { useDebounce } from '../hooks/useDebounce';

interface Checklist {
  id: number;
  title: string;
}

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  location?: { id: number; name: string };
  department?: { id: number; name: string };
}

interface Location {
  id: number;
  name: string;
}

interface Department {
  id: number;
  name: string;
}

interface ChecklistCompletion {
  id: number;
  checklist: number;
  checklist_title: string;
  user: User;
  user_location?: string | null;
  user_department?: string | null;
  status: string;
  started_at: string;
  completed_at: string | null;
  total_items: number;
  completed_items: number;
  completion_percentage: number;
  created_at: string;
  updated_at: string;
}

const STATUS_OPTIONS = [
  { value: 'completed', label: 'Completed' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'not_started', label: 'Not Started' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'cancelled', label: 'Cancelled' },
];

const ChecklistCompletions: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Data states
  const [completions, setCompletions] = useState<ChecklistCompletion[]>([]);
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter options
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  
  // Filter states (read from URL params)
  const [checklistIds, setChecklistIds] = useState<number[]>(() => {
    const ids = searchParams.getAll('checklistIds[]').map(Number).filter(n => !isNaN(n));
    return ids;
  });
  
  const [assigneeSearch, setAssigneeSearch] = useState(() => 
    searchParams.get('assigneeSearch') || ''
  );
  const debouncedAssigneeSearch = useDebounce(assigneeSearch, 300);
  
  const [assigneeId, setAssigneeId] = useState<number | null>(() => {
    const id = searchParams.get('assigneeId');
    return id ? parseInt(id, 10) : null;
  });
  
  const [locationIds, setLocationIds] = useState<number[]>(() => {
    const ids = searchParams.getAll('locationIds[]').map(Number).filter(n => !isNaN(n));
    return ids;
  });
  
  const [departmentIds, setDepartmentIds] = useState<number[]>(() => {
    const ids = searchParams.getAll('departmentIds[]').map(Number).filter(n => !isNaN(n));
    return ids;
  });
  
  const [status, setStatus] = useState<string>(() => 
    searchParams.get('status') || ''
  );
  
  const [completedFrom, setCompletedFrom] = useState<string>(() => 
    searchParams.get('completedFrom') || ''
  );
  
  const [completedTo, setCompletedTo] = useState<string>(() => 
    searchParams.get('completedTo') || ''
  );
  
  // Pagination states
  const [page, setPage] = useState<number>(() => {
    const p = searchParams.get('page');
    return p ? parseInt(p, 10) - 1 : 0;
  });
  
  const [rowsPerPage, setRowsPerPage] = useState<number>(() => {
    const ps = searchParams.get('pageSize');
    return ps ? parseInt(ps, 10) : 25;
  });
  
  const [sortBy, setSortBy] = useState<string>(() => 
    searchParams.get('sortBy') || 'completed_at'
  );
  
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(() => {
    const dir = searchParams.get('sortDir');
    return (dir === 'asc' || dir === 'desc') ? dir : 'desc';
  });
  
  // Update URL params when filters change
  const updateURLParams = useCallback(() => {
    const params = new URLSearchParams();
    
    checklistIds.forEach(id => params.append('checklistIds[]', String(id)));
    if (assigneeId) params.set('assigneeId', String(assigneeId));
    locationIds.forEach(id => params.append('locationIds[]', String(id)));
    departmentIds.forEach(id => params.append('departmentIds[]', String(id)));
    if (status) params.set('status', status);
    if (completedFrom) params.set('completedFrom', completedFrom);
    if (completedTo) params.set('completedTo', completedTo);
    if (page > 0) params.set('page', String(page + 1));
    if (rowsPerPage !== 25) params.set('pageSize', String(rowsPerPage));
    if (sortBy !== 'completed_at') params.set('sortBy', sortBy);
    if (sortDir !== 'desc') params.set('sortDir', sortDir);
    
    setSearchParams(params, { replace: true });
  }, [checklistIds, assigneeId, locationIds, departmentIds, status, completedFrom, completedTo, page, rowsPerPage, sortBy, sortDir, setSearchParams]);
  
  // Fetch filter options
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        // Fetch checklists
        const checklistsRes = await api.get('/checklists/');
        setChecklists(checklistsRes.data.results || checklistsRes.data || []);
        
        // Fetch users
        const usersRes = await api.get('/users/');
        setUsers(usersRes.data.results || usersRes.data || []);
        
        // Fetch locations
        const locationsRes = await api.get('/locations/');
        setLocations(locationsRes.data.results || locationsRes.data || []);
        
        // Fetch departments
        const departmentsRes = await api.get('/departments/');
        setDepartments(departmentsRes.data.results || departmentsRes.data || []);
      } catch (err) {
        console.error('Error fetching filter options:', err);
      }
    };
    
    fetchFilterOptions();
  }, []);
  
  // Fetch completions
  useEffect(() => {
    const fetchCompletions = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        
        checklistIds.forEach(id => params.append('checklistIds[]', String(id)));
        if (assigneeId) params.append('assigneeId', String(assigneeId));
        locationIds.forEach(id => params.append('locationIds[]', String(id)));
        departmentIds.forEach(id => params.append('departmentIds[]', String(id)));
        if (status) params.append('status', status);
        if (completedFrom) params.append('completedFrom', completedFrom);
        if (completedTo) params.append('completedTo', completedTo);
        
        params.append('page', String(page + 1));
        params.append('page_size', String(rowsPerPage));
        params.append('sortBy', sortBy);
        params.append('sortDir', sortDir);
        
        const response = await api.get(`/admin/checklist-completions?${params.toString()}`);
        setCompletions(response.data.results || response.data);
        setCount(response.data.count || (response.data.results || response.data).length);
        setError(null);
      } catch (err: any) {
        setError('Failed to load completions. Please try again.');
        console.error('Error fetching completions:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCompletions();
    updateURLParams();
  }, [checklistIds, assigneeId, locationIds, departmentIds, status, completedFrom, completedTo, page, rowsPerPage, sortBy, sortDir, updateURLParams]);
  
  // Filter users based on search
  const filteredUsers = React.useMemo(() => {
    if (!debouncedAssigneeSearch) return users;
    const search = debouncedAssigneeSearch.toLowerCase();
    return users.filter(user => 
      user.email.toLowerCase().includes(search) ||
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(search)
    );
  }, [users, debouncedAssigneeSearch]);
  
  const handleExportCsv = async () => {
    try {
      const query = searchParams.toString();
      const response = await api.get(`/admin/checklist-completions.csv?${query}`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'checklist_completions.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export completions. Please try again.');
      console.error('Error exporting completions:', err);
    }
  };
  
  const handleViewDetail = (completionId: number) => {
    navigate(`/checklists/completions/${completionId}`);
  };
  
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };
  
  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };
  
  const getStatusColor = (status: string) => {
    const colors: { [key: string]: any } = {
      completed: 'success',
      in_progress: 'info',
      not_started: 'default',
      overdue: 'error',
      cancelled: 'default',
    };
    return colors[status] || 'default';
  };
  
  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
    setPage(0);
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4" component="h1">Review Completions</Typography>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCsv}>
          Export CSV
        </Button>
      </Stack>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            {/* Checklist Filter */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Checklist</InputLabel>
                <Select
                  multiple
                  value={checklistIds}
                  label="Checklist"
                  onChange={(e) => {
                    setChecklistIds(e.target.value as number[]);
                    setPage(0);
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as number[]).map((id) => {
                        const checklist = checklists.find(c => c.id === id);
                        return checklist ? (
                          <Chip key={id} label={checklist.title} size="small" />
                        ) : null;
                      })}
                    </Box>
                  )}
                >
                  {checklists.map((checklist) => (
                    <MenuItem key={checklist.id} value={checklist.id}>
                      {checklist.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Assignee Filter */}
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={filteredUsers}
                getOptionLabel={(option) => `${option.first_name} ${option.last_name} (${option.email})`}
                value={users.find(u => u.id === assigneeId) || null}
                onChange={(_, newValue) => {
                  setAssigneeId(newValue ? newValue.id : null);
                  setAssigneeSearch('');
                  setPage(0);
                }}
                inputValue={assigneeSearch}
                onInputChange={(_, newValue) => setAssigneeSearch(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assignee"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                )}
                noOptionsText={debouncedAssigneeSearch ? 'No users found' : 'Type to search...'}
              />
            </Grid>
            
            {/* Location Filter */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  multiple
                  value={locationIds}
                  label="Location"
                  onChange={(e) => {
                    setLocationIds(e.target.value as number[]);
                    setPage(0);
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as number[]).map((id) => {
                        const location = locations.find(l => l.id === id);
                        return location ? (
                          <Chip key={id} label={location.name} size="small" />
                        ) : null;
                      })}
                    </Box>
                  )}
                >
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Department Filter */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  multiple
                  value={departmentIds}
                  label="Department"
                  onChange={(e) => {
                    setDepartmentIds(e.target.value as number[]);
                    setPage(0);
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as number[]).map((id) => {
                        const dept = departments.find(d => d.id === id);
                        return dept ? (
                          <Chip key={id} label={dept.name} size="small" />
                        ) : null;
                      })}
                    </Box>
                  )}
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Status Filter */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  label="Status"
                  onChange={(e) => {
                    setStatus(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Date Range Filters */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Completed From"
                type="date"
                value={completedFrom}
                onChange={(e) => {
                  setCompletedFrom(e.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Completed To"
                type="date"
                value={completedTo}
                onChange={(e) => {
                  setCompletedTo(e.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            {/* Clear Filters */}
            <Grid item xs={12}>
              <Button
                variant="text"
                onClick={() => {
                  setChecklistIds([]);
                  setAssigneeId(null);
                  setAssigneeSearch('');
                  setLocationIds([]);
                  setDepartmentIds([]);
                  setStatus('');
                  setCompletedFrom('');
                  setCompletedTo('');
                  setPage(0);
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      {/* Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => handleSort('completed_at')}
                      sx={{ textTransform: 'none', fontWeight: 'bold' }}
                    >
                      Completion Date {sortBy === 'completed_at' && (sortDir === 'asc' ? '↑' : '↓')}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => handleSort('checklist')}
                      sx={{ textTransform: 'none', fontWeight: 'bold' }}
                    >
                      Checklist
                    </Button>
                  </TableCell>
                  <TableCell>Assignee</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => handleSort('completion_percentage')}
                      sx={{ textTransform: 'none', fontWeight: 'bold' }}
                    >
                      Score {sortBy === 'completion_percentage' && (sortDir === 'asc' ? '↑' : '↓')}
                    </Button>
                  </TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {completions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No completions found. Try adjusting your filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  completions.map((completion) => (
                    <TableRow key={completion.id} hover>
                      <TableCell>{formatDateTime(completion.completed_at)}</TableCell>
                      <TableCell>{completion.checklist_title}</TableCell>
                      <TableCell>
                        {completion.user.first_name} {completion.user.last_name}
                        <Typography variant="caption" color="text.secondary" display="block">
                          {completion.user.email}
                        </Typography>
                      </TableCell>
                      <TableCell>{completion.user_location || completion.user.location?.name || '-'}</TableCell>
                      <TableCell>{completion.user_department || completion.user.department?.name || '-'}</TableCell>
                      <TableCell>
                        {completion.completion_percentage}%
                        <Typography variant="caption" color="text.secondary" display="block">
                          {completion.completed_items}/{completion.total_items} items
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={STATUS_OPTIONS.find(s => s.value === completion.status)?.label || completion.status}
                          color={getStatusColor(completion.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetail(completion.id)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            component="div"
            count={count}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </>
      )}
      
      <Box mt={2}>
        <Button variant="outlined" onClick={() => navigate('/checklists')}>
          Back to Checklists
        </Button>
      </Box>
    </Box>
  );
};

export default ChecklistCompletions;